import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Translate, withLocalize } from 'react-localize-redux';
import { SENTIMENTS, STARTER_CARDS, TRANSLATION_KEY } from '../constants';
import css from './WatchlistStarterCards.css';

const STARTERS_KEY = `${TRANSLATION_KEY}.EMPTY_STATE.STARTERS`;

const InstallDropPreview = () => (
    <>
        <div className={css.metricLabel}>
            <Translate id={`${STARTERS_KEY}.INSTALL_DROP_METRIC`} />
        </div>
        <div className={css.metricValue}>6,120</div>
        <div className={classNames(css.metricDelta, css.bad)}>
            <Translate id={`${STARTERS_KEY}.INSTALL_DROP_DELTA`} />
        </div>
        <svg className={css.chart} viewBox="0 0 86 42" fill="none" preserveAspectRatio="none">
            <path
                className={css.strokeBad}
                d="M2 12 L16 10 L30 14 L44 11 L58 18 L72 27 L84 36"
                strokeWidth="0.85"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <circle className={css.fillBad} cx="84" cy="36" r="1.3" />
        </svg>
    </>
);

const BID_GROUPS = [
    {
        nameKey: 'BID_CHANGE_GROUP_A',
        delta: '+31%',
        pre: 'M2 8.2 L10 8.8 L18 8.4',
        post: 'M18 8.4 L30 5.6 L46 3.6',
        dot: { cx: 46, cy: 3.6 },
    },
    {
        nameKey: 'BID_CHANGE_GROUP_B',
        delta: '+24%',
        pre: 'M2 9.6 L10 9.2 L18 9.5',
        post: 'M18 9.5 L30 7.4 L46 5.8',
        dot: { cx: 46, cy: 5.8 },
    },
    {
        nameKey: 'BID_CHANGE_GROUP_C',
        delta: '+18%',
        pre: 'M2 10.4 L10 10.8 L18 10.5',
        post: 'M18 10.5 L30 9.2 L46 7.8',
        dot: { cx: 46, cy: 7.8 },
    },
];

const BidChangePreview = () => (
    <>
        <div className={css.metricLabel}>
            <Translate id={`${STARTERS_KEY}.BID_CHANGE_METRIC`} />
        </div>
        <div className={css.metricValue}>+24%</div>
        <div className={classNames(css.metricDelta, css.good)}>
            <Translate id={`${STARTERS_KEY}.BID_CHANGE_DELTA`} />
        </div>
        <div className={css.sparkRows}>
            {BID_GROUPS.map(group => (
                <div key={group.nameKey} className={css.sparkRow}>
                    <span className={css.sparkName}>
                        <Translate id={`${STARTERS_KEY}.${group.nameKey}`} />
                    </span>
                    <svg className={css.sparkLine} viewBox="0 0 48 14" fill="none" preserveAspectRatio="none">
                        <path className={css.strokeMuted} d={group.pre} strokeWidth="1.1" strokeLinecap="round" />
                        <path
                            className={css.strokeGood}
                            d={group.post}
                            strokeWidth="1.1"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <circle className={css.fillGood} cx={group.dot.cx} cy={group.dot.cy} r="1.5" />
                    </svg>
                    <span className={classNames(css.sparkDelta, css.good)}>{group.delta}</span>
                </div>
            ))}
        </div>
    </>
);

const CREATIVE_ROWS = [
    { nameKey: 'CREATIVE_FATIGUE_ROW_B', ipm: '−21%', impressions: '+1%', flagged: true },
    { nameKey: 'CREATIVE_FATIGUE_ROW_C', ipm: '−24%', impressions: '−2%', flagged: true },
    { nameKey: 'CREATIVE_FATIGUE_ROW_D', ipm: '−4%', impressions: '+3%', flagged: false },
];

const CreativeFatiguePreview = () => (
    <div className={css.table}>
        <div className={css.tableHeader}>
            <span>
                <Translate id={`${STARTERS_KEY}.CREATIVE_FATIGUE_COL_CREATIVE`} />
            </span>
            <span>
                <Translate id={`${STARTERS_KEY}.CREATIVE_FATIGUE_COL_IPM`} />
            </span>
            <span>
                <Translate id={`${STARTERS_KEY}.CREATIVE_FATIGUE_COL_IMPRESSIONS`} />
            </span>
        </div>
        {CREATIVE_ROWS.map(row => (
            <div key={row.nameKey} className={css.tableRow}>
                <span className={classNames(css.rowName, { [css.muted]: !row.flagged })}>
                    <Translate id={`${STARTERS_KEY}.${row.nameKey}`} />
                </span>
                <span className={classNames(css.rowIpm, row.flagged ? css.bad : css.muted)}>{row.ipm}</span>
                <span className={css.muted}>{row.impressions}</span>
            </div>
        ))}
    </div>
);

const PREVIEWS = {
    installDrop: InstallDropPreview,
    bidChange: BidChangePreview,
    creativeFatigue: CreativeFatiguePreview,
};

const WatchlistStarterCards = ({ onPick, translate }) => (
    <div className={css.cards}>
        {STARTER_CARDS.map(({ id, sentiment, titleKey, promptKey }) => {
            const Preview = PREVIEWS[id];
            const isHeadsUp = sentiment === SENTIMENTS.HEADS_UP;
            return (
                <button key={id} type="button" className={css.card} onClick={() => onPick(translate(promptKey))}>
                    <div className={css.cardText}>
                        <div className={css.cardTitle}>
                            <Translate id={titleKey} />
                        </div>
                        <div className={css.cardDescription}>
                            “<Translate id={promptKey} />”
                        </div>
                    </div>
                    <div className={css.cardPreview} aria-hidden="true">
                        <div className={classNames(css.tag, isHeadsUp ? css.bad : css.good)}>
                            <span className={css.tagDot} />
                            <Translate id={`${TRANSLATION_KEY}.BADGES.${isHeadsUp ? 'HEADS_UP' : 'GOOD_NEWS'}`} />
                        </div>
                        <div className={css.previewBody}>
                            <Preview />
                        </div>
                    </div>
                </button>
            );
        })}
    </div>
);

WatchlistStarterCards.propTypes = {
    onPick: PropTypes.func.isRequired,
    translate: PropTypes.func.isRequired,
};

export default withLocalize(WatchlistStarterCards);
